import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AText } from '@alf/uikit';
import { REGISTER_COMPONENTS_TYPES } from '@namespace/auth';
import { StepperContext, usePhoneCode } from '@namespace/common';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder,
  useFormMethods
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import {
  NOTIFICATION_TYPES,
  NotificationContext,
  removeNotificationAction,
  useNotifications
} from '@namespace/notifications';
import {
  FORM_COMPONENTS_TYPES as LOCAL_FORM_COMPONENTS_TYPES,
  formComponentsMap
} from './components/constants';
import { useRegions } from './hooks/useRegions';
import {
  FORM_VALIDATION_TYPES as LOCAL_FORM_VALIDATION_TYPES,
  formValidationMap
} from './validators/constants';
import { pepAttributes, pinToStateGendersMap } from './constants';

import styles from './index.module.css';

const InfoForm = ({
  onStepSuccess,
  onGoBack,
  defaultCountryCode = '',
  classNames = {},
  isShowNeedHelp,
  ...props
}) => {
  const { customNotification } = useNotifications();
  const [{ notificationDispatch, notificationType }] =
    useContext(NotificationContext);

  const [shouldWaitForPinValidating, setShouldWaitForPinValidating] =
    useState(false);

  const datePickerOpenRef = useRef();
  const tryOpenDatePicker = () => {
    if (datePickerOpenRef.current) {
      datePickerOpenRef.current();
      datePickerOpenRef.current = null;
    }
  };

  const { t } = useI18n();
  const defaultPhoneCode = usePhoneCode(defaultCountryCode);
  const regions = useRegions();
  const { formClassName = '', inputWithoutLabel = '' } = classNames;
  const {
    stepper: {
      formData: {
        firstName = '',
        lastName = '',
        middleName = '',
        gender = 'mr',
        birthdate = '',
        phone = {
          isoCode: defaultCountryCode,
          number: defaultPhoneCode
        },
        pin = '',
        region = '',
        city = '',
        address = '',
        pep = false,
        pepGround = '',
        pepPosition = ''
      }
    }
  } = useContext(StepperContext);

  const defaultValues = {
    country: defaultCountryCode,
    firstName,
    lastName,
    middleName,
    birthdate,
    phone,
    gender,
    pin,
    region,
    city,
    address,
    pep,
    pepGround,
    pepPosition
  };

  const [showAllPepInfo, setShowAllPepInfo] = useState(false);

  const pepOptions = useMemo(
    () =>
      pepAttributes.map((attribute) => ({
        name: t(`personalOffice.profile.data.pepAttribute.${attribute}`),
        value: attribute
      })),
    [t]
  );

  const methods = useFormMethods({ defaultValues });
  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = methods;
  const watchPep = watch('pep');
  const watchPepGround = watch('pepGround');
  const watchRegion = watch('region');
  const watchCity = watch('city');

  const onClick = useCallback(() => {
    setShowAllPepInfo(!showAllPepInfo);
  }, [showAllPepInfo]);

  const onPinParsing = useCallback(
    (data) => {
      if (data) {
        clearErrors('gender');
        clearErrors('birthdate');

        setValue('gender', pinToStateGendersMap[data.gender]);
        setValue('birthdate', data.dateOfBirth);
      }
    },
    [clearErrors, setValue]
  );

  const handlePinInputFocus = useCallback(() => {
    datePickerOpenRef.current = null;

    setShouldWaitForPinValidating(true);
  }, []);

  const onPinValidation = useCallback((isPassed) => {
    const shouldOpenDatePicker = !isPassed && datePickerOpenRef.current;

    if (shouldOpenDatePicker) {
      tryOpenDatePicker();
    }
  }, []);

  const handlePinBlur = useCallback(
    (data) => {
      if (data?.parsed) {
        onPinParsing(data.parsed);
      } else if (data?.isValidationPassed !== undefined) {
        onPinValidation(data.isValidationPassed);
      }

      setShouldWaitForPinValidating(false);
      datePickerOpenRef.current = null;
    },
    [onPinParsing, onPinValidation]
  );

  const handleBeforeOpenFn = useCallback((fn) => {
    datePickerOpenRef.current = fn;
  }, []);

  const pepInfoLabel = showAllPepInfo ? (
    <AText color="txt_color_7" size="s" component="span">
      {t('personalOffice.profile.data.pepInfo')}
    </AText>
  ) : (
    <AText color="txt_color_7" size="s" component="span">
      {`${t('personalOffice.profile.data.cutPepInfo')} `}

      <AText
        color="txt_link"
        size="s"
        component="span"
        className={styles.showMore}
        onClick={onClick}
      >
        {t('personalOffice.profile.data.pepInfo.showMore')}
      </AText>
    </AText>
  );

  const componentsList = [
    {
      type: FORM_COMPONENTS_TYPES.REG_COUNTRY_SELECT,
      name: 'country',
      label: 'register.steps.personalInfo.selectCountry',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.NAME_INPUT,
      name: 'firstName',
      label: 'register.steps.personalInfo.enterPersonals',
      placeholder: 'register.steps.personalInfo.firstName',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.MIDDLE_NAME_INPUT,
      name: 'middleName',
      placeholder: 'register.steps.personalInfo.middleName',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      classNames: {
        wrapperClassName: inputWithoutLabel
      },
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.NAME_INPUT,
      name: 'lastName',
      placeholder: 'register.steps.personalInfo.lastName',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      classNames: {
        wrapperClassName: inputWithoutLabel
      },
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.PIN_CODE_INPUT,
      name: 'pin',
      placeholder: 'register.steps.personalInfo.pin',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        {
          type: FORM_VALIDATION_TYPES.MIN_LENGTH_VALIDATOR,
          parameters: [13, 'web.registration.validation.cnp']
        }
      ],
      isForAllCountries: true,
      parseOnBlur: true,
      onFocus: handlePinInputFocus,
      onBlur: handlePinBlur,
      size: 'm',
      classNames: {
        wrapperClassName: styles.pinWrapper
      }
    },
    {
      type: FORM_COMPONENTS_TYPES.REG_DATE_PICKER,
      name: 'birthdate',
      placeholder: 'register.steps.personalInfo.birthdate',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      classNames: {
        datePickerContainerClassName: inputWithoutLabel,
        datePickerWrapperClassName: ''
      },
      size: 'm',
      controlOpenExecution: shouldWaitForPinValidating && handleBeforeOpenFn
    },
    {
      type: FORM_COMPONENTS_TYPES.GENDER_BUTTONS_GROUP,
      name: 'gender',
      label: 'register.steps.personalInfo.gender',
      elements: [
        {
          label: 'register.steps.personalInfo.man',
          value: 'mr',
          iconName: 'icons/general/item/men'
        },
        {
          label: 'register.steps.personalInfo.woman',
          value: 'ms',
          iconName: 'icons/general/item/woman'
        }
      ],
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      classNames: {
        buttonClassName: styles.genderButton,
        buttonsContainer: styles.genderButtonsContainer
      },
      controlClickExecution: shouldWaitForPinValidating && handleBeforeOpenFn
    },
    {
      type: LOCAL_FORM_COMPONENTS_TYPES.REGIONS_SELECT,
      name: 'region',
      label: 'register.steps.welcome.personalInfo.address',
      regions,
      placeholder: 'web.registration.steps.personalInfo.regionplaceholder',
      inputClassName: watchRegion && styles.filledSelectInput,
      validationTypes: [
        {
          type: LOCAL_FORM_VALIDATION_TYPES.VALID_LIST_VALUE,
          parameters: [
            'web.registration.validation.region',
            { list: regions.map((i) => i.region) }
          ]
        }
      ],
      size: 'm'
    },
    {
      type: LOCAL_FORM_COMPONENTS_TYPES.CITIES_SELECT,
      name: 'city',
      regionsList: regions,
      region: watchRegion,
      disabled: !watchRegion || errors.region,
      placeholder: 'register.steps.personalInfo.city',
      classNames: { containerClassName: styles.citiesSelect },
      inputClassName: watchCity && styles.filledSelectInput,
      validationTypes: [
        {
          type: LOCAL_FORM_VALIDATION_TYPES.VALID_LIST_VALUE,
          parameters: [
            'web.registration.validation.city',
            {
              list: regions.find((i) => i.region === watchRegion)?.cities
            }
          ]
        }
      ],
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
      name: 'address',
      placeholder: 'register.steps.personalInfo.address',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        FORM_VALIDATION_TYPES.ADDRESS
      ],
      classNames: {
        wrapperClassName: inputWithoutLabel
      },
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.REG_PHONE,
      name: 'phone',
      label: 'register.steps.personalInfo.mobile',
      preventCodeChange: true,
      validationTypes: [
        {
          type: FORM_VALIDATION_TYPES.MIN_LENGTH_AND_CHECKPHONE_VALIDATOR,
          parameters: [3, 'validators.requiredValidator', (v) => v.number]
        }
      ],
      minNumberLength: 3,
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.CHECKBOX_FIELD,
      name: 'pep',
      label: 'personalOffice.profile.data.publicFigure',
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.INFO_MESSAGE,
      key: 'field-info-message-2',
      label: pepInfoLabel,
      classNames: { wrapperClassName: styles.pepPosition },
      icon: 'info'
    },
    watchPep && {
      type: FORM_COMPONENTS_TYPES.SELECT,
      name: 'pepGround',
      placeholder: 'personalOffice.profile.data.placeholder.pepGround',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      options: pepOptions
    },
    watchPep && {
      type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
      name: 'pepPosition',
      key: watchPepGround ? 'pepPosition' : 'disabledPepPosition',
      placeholder: 'personalOffice.profile.data.placeholder.pepPosition',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        FORM_VALIDATION_TYPES.NOT_EMPTY_STRING_VALIDATOR,
        {
          type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
          parameters: [256]
        }
      ],
      disabled: !watchPepGround
    },
    {
      type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
      value: 'register.steps.continue',
      size: 'xl'
    },
    {
      type: REGISTER_COMPONENTS_TYPES.BACK_BUTTON,
      onGoBack
    }
  ];

  if (isShowNeedHelp) {
    componentsList.push({
      type: FORM_COMPONENTS_TYPES.NEED_HELP,
      needHelpText: 'web.register.needhelp.link',
      dataRole: 'register-need-help'
    });
  }

  const pinErrorMessage = errors?.pin?.message;

  useEffect(() => {
    const isDateOfBirthNotificationActive =
      notificationType === NOTIFICATION_TYPES.DATE_OF_BIRTH;

    if (pinErrorMessage === 'accountingMessages.accounting_error_1547') {
      setValue('birthdate', '');
      customNotification({
        notificationType: NOTIFICATION_TYPES.DATE_OF_BIRTH,
        timeout: null
      });
    }

    return () => {
      if (isDateOfBirthNotificationActive) {
        notificationDispatch(removeNotificationAction({ notificationType }));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinErrorMessage]);

  const onSubmit = useCallback(
    (formData) => {
      const data = {
        ...formData,
        birthdate: methods.getValues('birthdate'),
        gender: methods.getValues('gender')
      };

      onStepSuccess(data);
    },
    [methods, onStepSuccess]
  );

  return (
    <FormBuilder
      wrapperClassName={formClassName}
      components={componentsList}
      componentsMap={formComponentsMap}
      formConfig={{ defaultValues }}
      onSubmit={onSubmit}
      formMethods={methods}
      validationMap={formValidationMap}
      formProps={{
        defaultCountryCode,
        ...props
      }}
    />
  );
};

export default InfoForm;
